import { PaymentAuthorizationStatus, TransactionStatus } from '@ankor-io/common/types/transaction'

export enum ApprovalWorkflowType {
  TRANSACTIONS = 'TRANSACTIONS',
  PAYMENTS = 'PAYMENTS',
}

/** Maximum number of stages on one approval workflow rule. */
export const MAX_APPROVAL_WORKFLOW_STAGES = 3

/**
 * Statuses that can block a workflow edit, delete, or currency change.
 * Incomplete, submitted, and partially approved are still on a path — treat
 * them as live work. Approved and rejected are done: leftover state can be
 * cleared, and they never block a save.
 */
export const IN_FLIGHT_APPROVAL_WORKFLOW_TRANSACTION_STATUSES: readonly TransactionStatus[] = [
  TransactionStatus.INCOMPLETE,
  TransactionStatus.SUBMITTED,
  TransactionStatus.PARTIALLY_APPROVED,
]

/**
 * Payment authorization statuses that block a payments workflow edit, delete, or settings change.
 * Awaiting authorization and partially authorized still have a state on the workflow.
 * Awaiting initiator has no state yet. Resubmission required is waiting on the initiator and does not block.
 */
export const IN_FLIGHT_PAYMENT_AUTHORIZATION_STATUSES: readonly PaymentAuthorizationStatus[] = [
  PaymentAuthorizationStatus.AWAITING_AUTHORIZATION,
  PaymentAuthorizationStatus.PARTIALLY_AUTHORIZED,
]

// How many in-flight expenses we put on a 409. Fetch one extra so `hasMore` is exact.
export const IN_FLIGHT_CONFLICT_LIST_TAKE = 20

/**
 * An approval workflow (default or custom team) scoped to a vessel and connected account.
 */
export type ClientApprovalWorkflow = {
  id: string
  vesselId: string
  connectedAccountId: string
  type: ApprovalWorkflowType
  name: string
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

/**
 * A spender assigned as an initiator of a custom approval workflow.
 */
export type ClientApprovalWorkflowInitiator = {
  id: string
  userId: string
  name: string
}

/**
 * An amount-band rule within a workflow, including the catch-all.
 */
export type ClientApprovalWorkflowRule = {
  id: string
  isCatchAll: boolean
  amountCeilingCents?: number
}

/**
 * A sequential approval stage within a rule.
 * Order is the array order returned by the API. The client does not send or read stage numbers.
 */
export type ClientApprovalWorkflowRuleStage = {
  id: string
  isAutoApprove: boolean
}

/**
 * An approver assigned to a rule stage.
 */
export type ClientApprovalWorkflowApprover = {
  id: string
  userId: string
  name: string
}

/**
 * A rule stage with its approver rows loaded.
 */
export type ClientApprovalWorkflowRuleStageWithApprovers = ClientApprovalWorkflowRuleStage & {
  approvers: ClientApprovalWorkflowApprover[]
}

/**
 * A rule with stages and approvers loaded.
 */
export type ClientApprovalWorkflowRuleWithStages = ClientApprovalWorkflowRule & {
  stages: ClientApprovalWorkflowRuleStageWithApprovers[]
}

/**
 * A workflow with its initiators, rules, stages, and approvers loaded.
 */
export type ClientFullApprovalWorkflow = ClientApprovalWorkflow & {
  initiators: ClientApprovalWorkflowInitiator[]
  rules: ClientApprovalWorkflowRuleWithStages[]
}

export type ClientApprovalWorkflowInitiatorCandidate = {
  userId: string
  name: string
  email: string
}

/**
 * A sequential approval stage on create.
 * Stage order is the array order. The client does not send stage numbers.
 */
export type CreateApprovalWorkflowStageBody = {
  isAutoApprove: boolean
  approverUserIds: string[]
}

/**
 * An amount-band rule on create, including the catch-all.
 */
export type CreateApprovalWorkflowRuleBody = {
  isCatchAll: boolean
  amountCeilingCents?: number
  stages: CreateApprovalWorkflowStageBody[]
}

/**
 * A sequential approval stage on update.
 * Stage order is the array order. The client does not send stage numbers.
 * The id is only sent on update.
 */
export type ApprovalWorkflowStageBody = {
  id?: string
  isAutoApprove: boolean
  approverUserIds: string[]
}

/**
 * An amount-band rule on update, including the catch-all.
 * The id is only sent on update.
 */
export type ApprovalWorkflowRuleBody = {
  id?: string
  isCatchAll: boolean
  amountCeilingCents?: number
  stages: ApprovalWorkflowStageBody[]
}

/**
 * POST Body for creating an approval workflow.
 */
export type CreateApprovalWorkflowBody = {
  type: ApprovalWorkflowType
  name: string
  isDefault: boolean
  initiatorUserIds: string[]
  rules: CreateApprovalWorkflowRuleBody[]
}

/**
 * PUT Body for updating an approval workflow.
 */
export type UpdateApprovalWorkflowBody = {
  name?: string
  isDefault?: boolean
  type?: ApprovalWorkflowType
  initiatorUserIds: string[]
  rules: ApprovalWorkflowRuleBody[]
}

/**
 * Per-connected-account approval settings for a vessel.
 */
export type ClientApprovalWorkflowSettings = {
  id: string
  vesselId: string
  connectedAccountId: string
  type: ApprovalWorkflowType
  currencyCode: string
  approversCanApproveOwn: boolean
  isForceApprovalEnabled: boolean
  areWorkflowsEnabled: boolean
  updatedAt: string
}

/**
 * One inspect outcome for approve, reject, or force.
 * Wire format for GET /transaction/:transactionId/status/inspect.
 */
export type InspectApprovalAction = {
  allowed: boolean
  reason?: string
  action: 'legacy' | 'reject' | 'force' | 'stage-approve' | 'initiate' | 'denied'
}

export type ExpenseInspectApprovalAction = InspectApprovalAction & {
  nextStatus?: TransactionStatus
}

export type ExpenseInspectApprovalActionResult = {
  approve: ExpenseInspectApprovalAction
  reject: ExpenseInspectApprovalAction
  force: ExpenseInspectApprovalAction
}

/**
 * One inspect outcome for a payment authorization. The status is the payment axis,
 * not the expense status.
 */
export type PaymentInspectApprovalAction = InspectApprovalAction & {
  nextStatus?: PaymentAuthorizationStatus
}

export type PaymentInspectApprovalActionResult = {
  approve: PaymentInspectApprovalAction
  reject: PaymentInspectApprovalAction
  force: PaymentInspectApprovalAction
  /**
   * Present only while the payment is waiting on an initiator.
   * Null once authorisation has started, or the payment is already authorised.
   */
  initiate: PaymentInspectApprovalAction | null
}

/**
 * Both approval axes for one expense in a single inspect response.
 * Wire format for GET /transaction/:transactionId/status/inspect once type is dropped.
 * Commit (POST status) stays typed per axis.
 */
export type CombinedInspectApprovalActionResult = {
  expense: ExpenseInspectApprovalActionResult
  payment: PaymentInspectApprovalActionResult
}

/**
 * One in-flight expense returned when a change is blocked.
 * Wire format for the pending-approvals modal: vendor, amount, status, and current stage.
 * `amountCents` and `currencyCode` are the original spend, not the charter-converted ledger amount.
 * `id` is for the detail link. The rule path name is already on the editor.
 */
export type InFlightTransactionPayload = {
  id: string
  status: TransactionStatus
  vendor?: string
  amountCents: number
  currencyCode: string
  stageNumber: number
  stageCount: number
}

/**
 * 409 `info` when a change cannot proceed because these expenses are still in flight.
 * `transactions` is capped at `IN_FLIGHT_CONFLICT_LIST_TAKE`. `hasMore` is true when
 * that cap hid further rows.
 */
export type InFlightConflictPayload = {
  transactions: InFlightTransactionPayload[]
  hasMore: boolean
}

/**
 * One in-flight payment returned when a payments workflow change is blocked.
 * `status` is the payment authorization status. The expense status is already approved.
 */
export type InFlightPaymentPayload = {
  id: string
  status: PaymentAuthorizationStatus
  vendor?: string
  amountCents: number
  currencyCode: string
  stageNumber: number
  stageCount: number
}

/**
 * 409 `info` when a change cannot proceed because these payments are still in flight.
 * `transactions` may be capped. `hasMore` is true when further rows were hidden.
 */
export type InFlightPaymentConflictPayload = {
  transactions: InFlightPaymentPayload[]
  hasMore: boolean
}

/**
 * Body for POST .../transaction/:transactionId/payment-initiation.
 * The server fills amounts, currency, reason, and request id from the expense and beneficiary.
 */
export type InitiatePaymentBody = {
  beneficiaryId: string
  reference: string
}
